import { Link, useLocation } from 'react-router-dom';

interface BreadcrumbItem {
  name: string;
  href?: string;
}

interface BreadcrumbProps {
  items?: BreadcrumbItem[];
}

export default function Breadcrumb({ items }: BreadcrumbProps) {
  const location = useLocation();
  
  // Auto-generate breadcrumb if items not provided
  const generateBreadcrumb = (): BreadcrumbItem[] => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const breadcrumbItems: BreadcrumbItem[] = [{ name: 'Home', href: '/' }];
    
    let currentPath = '';
    
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      
      let name = segment;
      
      // Convert URL segments to readable names
      switch (segment) {
        case 'about':
          name = 'About';
          break;
        case 'wisdom':
          name = '執事の知恵';
          break;
        case 'services':
          name = 'Services';
          break;
        case 'media':
          name = 'Media';
          break;
        case 'contact':
          name = 'Contact';
          break;
        case 'hospitality':
          name = 'おもてなしの心';
          break;
        case 'etiquette':
          name = 'フォーマルマナー';
          break;
        case 'butler-skills':
          name = '執事スキル';
          break;
        case 'elegance':
          name = '日常の品格';
          break;
        case 'corporate-training':
          name = '企業研修・セミナー';
          break;
        case 'private-butler':
          name = 'プライベート執事サービス';
          break;
        case 'manner-course':
          name = 'マナー講座';
          break;
        case 'consulting':
          name = 'コンサルティング';
          break;
        case 'articles':
          name = '記事';
          break;
        case 'vip-service':
          name = 'VIP対応の極意';
          break;
        case 'communication':
          name = 'コミュニケーション術';
          break;
        case 'mindset':
          name = 'おもてなしの心構え';
          break;
        case 'table-manners':
          name = 'テーブルマナーの美学';
          break;
        case 'dress-code':
          name = 'ドレスコードの基本';
          break;
        case 'social-etiquette':
          name = '社交のエチケット';
          break;
        case 'daily-routine':
          name = '執事の一日';
          break;
        case 'observation':
          name = '観察力の磨き方';
          break;
        case 'time-management':
          name = '時間管理の技術';
          break;
        case 'posture':
          name = '美しい所作';
          break;
        case 'speech':
          name = '言葉遣いと話し方';
          break;
        case 'organization':
          name = '整理整頓の哲学';
          break;
        default:
          name = segment.charAt(0).toUpperCase() + segment.slice(1);
      }
      
      // Don't add href for the last item (current page)
      const isLastItem = index === pathSegments.length - 1;
      breadcrumbItems.push({
        name,
        href: isLastItem ? undefined : currentPath
      });
    });
    
    return breadcrumbItems;
  };
  
  const breadcrumbItems = items || generateBreadcrumb();
  
  // Don't show breadcrumb on home page
  if (location.pathname === '/') {
    return null;
  }
  
  return (
    <nav className="bg-[#f5f5f5] py-6 border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-8">
        <ol className="flex items-center space-x-2 text-sm">
          {breadcrumbItems.map((item, index) => (
            <li key={index} className="flex items-center">
              {index > 0 && (
                <i className="ri-arrow-right-s-line text-gray-400 mx-2"></i>
              )}
              
              {item.href ? (
                <Link
                  to={item.href}
                  className="text-gray-600 hover:text-[#917c50] transition-colors duration-300 cursor-pointer"
                  style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
                >
                  {item.name}
                </Link>
              ) : (
                <span 
                  className="text-[#917c50] font-medium"
                  style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
                >
                  {item.name}
                </span>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}
